import { hideLoadingScreen, router, showLoadingScreen } from "..";
import { getPrices } from "../api/prices";
import {
	getSmartplug,
	putSmartplugPowerEnabled,
} from "../api/smartplugs_{imei}";
import { deviceCard, formatSubscriptionStatus } from "../device/device_card";
import { locale, relativeTimeString, t } from "../locale";
import { addNotificationListener } from "../mobile";
import { state } from "../state";
import {
	errorToast,
	keyValueParagraph,
	successToast,
} from "../view/components";
import { Html } from "../view/html";
import { MaterialDesign } from "../view/material_design";
import { getSmartplugWarnings } from "./sp_warnings";

/**
 * @param {Smartplug} sp
 */
async function formatSubscriptionPlan(sp) {
	if (sp.Subscription.Trialing) return locale.strings.trial;

	const { SmartplugMonth, SmartplugYear } = await getPrices();

	switch (sp.Subscription.PriceIds.Current) {
		case SmartplugMonth.Id:
			return locale.string({
				deDE: "Monatlich",
				enGB: "Monthly",
				frFR: "Mensuel",
				svSE: "Månadsvis",
			});
		case SmartplugYear.Id:
			return locale.string({
				deDE: "Jährlich",
				enGB: "Yearly",
				frFR: "Annuel",
				svSE: "Årsvis",
			});
	}

	const expiryTime = new Date(sp.Subscription.ExpiresAt);
	if (Date.now() < expiryTime.getTime()) return "";

	return locale.strings.none;
}

/**
 * @param {Smartplug} sp
 */
async function smartplugCard(sp) {
	addNotificationListener("smartplug", sp.Imei, () => {
		getSmartplug(sp.Imei).then((sp) => {
			state.smartplugs[sp.Imei] = sp;
		});
	});

	const togglePower = () => {
		showLoadingScreen();
		putSmartplugPowerEnabled(
			sp.Imei,
			!state.smartplugs[sp.Imei].PowerEnabled,
		)
			.then((sp) => {
				if (sp.PowerEnabled) powerOffIcon.replaceWith(powerOnIcon);
				else powerOnIcon.replaceWith(powerOffIcon);

				state.smartplugs[sp.Imei] = sp;
				successToast();
			})
			.catch(errorToast)
			.finally(hideLoadingScreen);
	};

	const powerOnIcon = MaterialDesign.icon({
		id: "flash_on",
		onClick: togglePower,
	});

	const powerOffIcon = MaterialDesign.icon({
		id: "flash_off",
		onClick: togglePower,
	});

	return deviceCard({
		deviceName: sp.Metadata.Name,
		deviceType: "smartplug",
		productName: "SmartPlug",
		warnings: getSmartplugWarnings(sp),
		lines: [
			keyValueParagraph(
				locale.string({
					enGB: "Device status",
					deDE: "Gerätestatus",
					frFR: "État de l'appareil",
					svSE: "Enhetsstatus",
				}),
			),
			keyValueParagraph(
				locale.string({
					deDE: "- Strom",
					enGB: "- Power",
					frFR: "- Alimentation",
					svSE: "- Ström",
				}),
				sp.PowerEnabled
					? locale.string({
							deDE: "Ein",
							enGB: "On",
							frFR: "Activé",
							svSE: "På",
						})
					: locale.string({
							deDE: "Aus",
							enGB: "Off",
							frFR: "Désactivé",
							svSE: "Av",
						}),
			),
			keyValueParagraph(
				locale.string({
					deDE: "- Belastung",
					enGB: "- Load",
					frFR: "- Charge",
					svSE: "- Last",
				}),
				`${sp.Load} W`,
			),
			keyValueParagraph(
				locale.string({
					deDE: "- Verbrauch",
					enGB: "- Consumption",
					frFR: "- Consommation",
					svSE: "- Förbrukning",
				}),
				`${sp.Consumption} Wh`,
			),
			keyValueParagraph(
				locale.string({
					deDE: "- Temperatur",
					enGB: "- Temperature",
					frFR: "- Température",
					svSE: "- Temperatur",
				}),
				sp.Temperature >= -20 ? `${sp.Temperature} °C` : "-",
			),
			keyValueParagraph(
				locale.string({
					deDE: "- Signalstärke",
					enGB: "- Signal strength",
					frFR: "- Force de signal",
					svSE: "- Signalstyrka",
				}),
				`${sp.SignalStrength}%`,
			),
			keyValueParagraph(
				t("deviceCard.lastActive")(),
				relativeTimeString(sp.LastUplinkAt),
			),
			keyValueParagraph(locale.strings.subscription),
			keyValueParagraph(
				locale.string({
					enGB: "- Plan",
					deDE: "- Plan",
					frFR: "- Plan",
					svSE: "- Plan",
				}),
				await formatSubscriptionPlan(sp),
			),
			keyValueParagraph(...formatSubscriptionStatus(sp.Subscription)),
		],
		buttons: [
			Html.anchor({}, sp.PowerEnabled ? powerOnIcon : powerOffIcon),
			Html.anchor(
				{},
				MaterialDesign.icon({
					id: "monitoring",
					onClick: () => router.jump(`/smartplug/${sp.Imei}/logs`),
				}),
			),
			Html.anchor(
				{},
				MaterialDesign.icon({
					id: "settings",
					onClick: () => router.jump(`/smartplug/${sp.Imei}`),
				}),
			),
		],
	});
}

export { smartplugCard };
